import React, { useState, useEffect, useContext } from 'react';
import { AuthContext, API } from '../App';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Zap, Battery, Clock, IndianRupee, Car, FileText, 
  Plus, TrendingUp, Calendar, Receipt, Printer
} from 'lucide-react';
import BillTemplate from '../components/BillTemplate';

const ECharging = () => {
  const { token } = useContext(AuthContext);
  const [settings, setSettings] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showBillDialog, setShowBillDialog] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [billSettings, setBillSettings] = useState(null);
  const [pumpProfile, setPumpProfile] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    charging_point_id: '',
    date: new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' }),
    customer_name: '',
    customer_mobile: '',
    vehicle_number: '',
    vehicle_type: '',
    energy_consumed_kwh: '',
    charging_duration_mins: '',
    idle_duration_mins: '0',
    payment_method: 'cash',
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    try {
      const [settingsRes, transactionsRes, dashboardRes, billRes] = await Promise.all([
        fetch(`${API}/e-charging/settings`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/e-charging/transactions`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/e-charging/dashboard`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/bill-settings`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      if (settingsRes.ok) setSettings(await settingsRes.json());
      if (transactionsRes.ok) {
        const data = await transactionsRes.json();
        setTransactions(data.transactions || []);
      }
      if (dashboardRes.ok) setDashboardStats(await dashboardRes.json());
      if (billRes.ok) {
        const billData = await billRes.json();
        setBillSettings(billData.bill_settings);
        setPumpProfile(billData.pump_profile);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const calculateBill = () => {
    if (!settings || !formData.energy_consumed_kwh) return null;
    
    const energy = parseFloat(formData.energy_consumed_kwh) || 0;
    const idleMins = parseFloat(formData.idle_duration_mins) || 0;
    
    const energyCost = energy * (settings.price_per_kwh || 12);
    const idleCharges = idleMins * (settings.idle_charge_per_min || 2);
    const serviceCharge = settings.service_charge || 10;
    const subtotal = energyCost + idleCharges + serviceCharge;
    const gstAmount = subtotal * ((settings.gst_rate || 18) / 100);
    const total = subtotal + gstAmount;
    const gridCost = energy * (settings.grid_cost_per_kwh || 8);
    const profit = total - gridCost;
    
    return { energyCost, idleCharges, serviceCharge, subtotal, gstAmount, total, gridCost, profit };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.charging_point_id || !formData.energy_consumed_kwh) {
      toast.error('Please select charging point and enter energy consumed');
      return;
    }
    
    try {
      const response = await fetch(`${API}/e-charging/transaction`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          ...formData,
          date: new Date(formData.date).toISOString(),
          energy_consumed_kwh: parseFloat(formData.energy_consumed_kwh),
          charging_duration_mins: parseFloat(formData.charging_duration_mins) || 0,
          idle_duration_mins: parseFloat(formData.idle_duration_mins) || 0
        })
      });
      
      if (response.ok) {
        const newTransaction = await response.json();
        toast.success(`Bill ${newTransaction.bill_number} generated successfully`);
        setSelectedTransaction(newTransaction);
        setShowBillDialog(true);
        fetchData();
        // Reset form
        setFormData({
          ...formData,
          customer_name: '',
          customer_mobile: '',
          vehicle_number: '',
          vehicle_type: '',
          energy_consumed_kwh: '',
          charging_duration_mins: '',
          idle_duration_mins: '0',
          notes: ''
        });
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create transaction');
      }
    } catch (error) {
      toast.error('Failed to create transaction');
    }
  };

  const handlePrint = () => {
    // Get the bill content element
    const billContent = document.getElementById('printable-bill');
    if (!billContent) {
      toast.error('Bill content not found');
      return;
    }
    
    // Create invoice number based on bill type
    const invoiceTitle = `E-ChargingInvoiceNo${selectedTransaction?.bill_number || ''}`;
    
    // Create a new window for printing
    const printWindow = window.open('', '_blank', 'width=400,height=600');
    
    // Write the print content
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>${invoiceTitle}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { 
              font-family: Arial, sans-serif; 
              padding: 20px; 
              max-width: 400px; 
              margin: 0 auto;
              color: #000;
              background: #fff;
            }
            .text-center { text-align: center; }
            .font-bold { font-weight: bold; }
            .font-mono { font-family: monospace; }
            .text-xs { font-size: 10px; }
            .text-sm { font-size: 12px; }
            .text-lg { font-size: 16px; }
            .text-xl { font-size: 18px; }
            .text-muted-foreground { color: #666; }
            .flex { display: flex; }
            .justify-between { justify-content: space-between; }
            .items-center { align-items: center; }
            .gap-2 { gap: 8px; }
            .mb-1 { margin-bottom: 4px; }
            .mb-2 { margin-bottom: 8px; }
            .mb-4 { margin-bottom: 16px; }
            .mt-2 { margin-top: 8px; }
            .mt-3 { margin-top: 12px; }
            .py-2 { padding-top: 8px; padding-bottom: 8px; }
            .py-3 { padding-top: 12px; padding-bottom: 12px; }
            .pt-2 { padding-top: 8px; }
            .pt-3 { padding-top: 12px; }
            .pb-4 { padding-bottom: 16px; }
            .space-y-1 > * + * { margin-top: 4px; }
            .space-y-2 > * + * { margin-top: 8px; }
            .space-y-4 > * + * { margin-top: 16px; }
            .border-b { border-bottom: 1px solid #ccc; }
            .border-t { border-top: 1px solid #ccc; }
            .border-dashed { border-style: dashed; }
            .border-b-2 { border-bottom: 2px solid #ccc; }
            .border-double { border-style: double; }
            .rounded { border-radius: 4px; }
            .bg-gray-100 { background-color: #f3f4f6; }
            .px-2 { padding-left: 8px; padding-right: 8px; }
            .px-3 { padding-left: 12px; padding-right: 12px; }
            .py-1 { padding-top: 4px; padding-bottom: 4px; }
            .bg-green-100 { background-color: #dcfce7; }
            .text-green-700 { color: #15803d; }
            img { max-height: 48px; width: auto; }
            .inline-block { display: inline-block; }
            @media print {
              body { padding: 10px; }
            }
          </style>
        </head>
        <body>
          ${billContent.innerHTML}
        </body>
      </html>
    `);
    
    printWindow.document.close();
    
    // Wait for content to load then print
    printWindow.onload = () => {
      printWindow.focus();
      printWindow.print();
      printWindow.close();
    };
  };

  const billPreview = calculateBill();

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (!settings?.enabled) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <Zap className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
          <h2 className="text-xl font-semibold mb-2">E-Charging Not Enabled</h2>
          <p className="text-muted-foreground">Please enable E-Charging in Settings to use this feature.</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Zap className="w-6 h-6 text-yellow-500" />
              E-Charging Station
            </h1>
            <p className="text-muted-foreground">Manage electric vehicle charging and billing</p>
          </div>
        </div>

        {/* Dashboard Stats */}
        {dashboardStats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="bg-gradient-to-br from-yellow-500/10 to-orange-500/10 border-yellow-500/20">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-yellow-600 mb-1">
                  <IndianRupee className="w-4 h-4" />
                  <span className="text-sm">Today Revenue</span>
                </div>
                <p className="text-2xl font-bold">₹{(dashboardStats.today?.revenue || 0).toLocaleString('en-IN')}</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 border-green-500/20">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-green-600 mb-1">
                  <TrendingUp className="w-4 h-4" />
                  <span className="text-sm">Month Revenue</span>
                </div>
                <p className="text-2xl font-bold">₹{(dashboardStats.month?.revenue || 0).toLocaleString('en-IN')}</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border-blue-500/20">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-blue-600 mb-1">
                  <Battery className="w-4 h-4" />
                  <span className="text-sm">Month Energy</span>
                </div>
                <p className="text-2xl font-bold">{(dashboardStats.month?.energy_kwh || 0).toFixed(1)} kWh</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 border-purple-500/20">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-purple-600 mb-1">
                  <Clock className="w-4 h-4" />
                  <span className="text-sm">Month Hours</span>
                </div>
                <p className="text-2xl font-bold">{(dashboardStats.month?.duration_hours || 0).toFixed(1)} hrs</p>
              </CardContent>
            </Card>
          </div>
        )}

        <Tabs defaultValue="billing" className="space-y-4">
          <TabsList>
            <TabsTrigger value="billing" className="flex items-center gap-2">
              <Receipt className="w-4 h-4" />
              New Billing
            </TabsTrigger>
            <TabsTrigger value="ledger" className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              E-Charging Ledger
            </TabsTrigger>
          </TabsList>

          <TabsContent value="billing">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Billing Form */}
              <Card className="flex flex-col max-h-[calc(100vh-250px)]">
                <CardHeader className="flex-shrink-0">
                  <CardTitle className="flex items-center gap-2">
                    <Plus className="w-5 h-5" />
                    New Charging Session
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex-1 overflow-y-auto">
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="col-span-2">
                        <Label>Charging Point *</Label>
                        <Select
                          value={formData.charging_point_id}
                          onValueChange={(value) => setFormData({...formData, charging_point_id: value})}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select charging point" />
                          </SelectTrigger>
                          <SelectContent>
                            {(settings.charging_points || []).map(cp => (
                              <SelectItem key={cp.id} value={cp.id}>
                                {cp.name} ({cp.connector_type} - {cp.power_output_kw}kW)
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div>
                        <Label>Date</Label>
                        <Input
                          type="date"
                          value={formData.date}
                          onChange={(e) => setFormData({...formData, date: e.target.value})}
                        />
                      </div>
                      
                      <div>
                        <Label>Energy Consumed (kWh) *</Label>
                        <Input
                          type="number"
                          step="0.1"
                          placeholder="e.g., 25.5"
                          value={formData.energy_consumed_kwh}
                          onChange={(e) => setFormData({...formData, energy_consumed_kwh: e.target.value})}
                        />
                      </div>
                      
                      <div>
                        <Label>Charging Duration (mins)</Label>
                        <Input
                          type="number"
                          placeholder="e.g., 45"
                          value={formData.charging_duration_mins}
                          onChange={(e) => setFormData({...formData, charging_duration_mins: e.target.value})}
                        />
                      </div>
                      
                      <div>
                        <Label>Idle Time (mins)</Label>
                        <Input
                          type="number"
                          placeholder="0"
                          value={formData.idle_duration_mins}
                          onChange={(e) => setFormData({...formData, idle_duration_mins: e.target.value})}
                        />
                      </div>
                      
                      <div>
                        <Label>Customer Name</Label>
                        <Input
                          placeholder="Optional"
                          value={formData.customer_name}
                          onChange={(e) => setFormData({...formData, customer_name: e.target.value})}
                        />
                      </div>
                      
                      <div>
                        <Label>Mobile Number</Label>
                        <Input
                          placeholder="Optional"
                          value={formData.customer_mobile}
                          onChange={(e) => setFormData({...formData, customer_mobile: e.target.value})}
                        />
                      </div>
                      
                      <div>
                        <Label>Vehicle Number</Label>
                        <Input
                          placeholder="e.g., MH12AB1234"
                          value={formData.vehicle_number}
                          onChange={(e) => setFormData({...formData, vehicle_number: e.target.value})}
                        />
                      </div>
                      
                      <div>
                        <Label>Vehicle Type</Label>
                        <Select
                          value={formData.vehicle_type}
                          onValueChange={(value) => setFormData({...formData, vehicle_type: value})}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Car">Car</SelectItem>
                            <SelectItem value="Bike">Bike</SelectItem>
                            <SelectItem value="Auto">Auto Rickshaw</SelectItem>
                            <SelectItem value="Bus">Bus</SelectItem>
                            <SelectItem value="Truck">Truck</SelectItem>
                            <SelectItem value="Other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div>
                        <Label>Payment Method</Label>
                        <Select
                          value={formData.payment_method}
                          onValueChange={(value) => setFormData({...formData, payment_method: value})}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="cash">Cash</SelectItem>
                            <SelectItem value="upi">UPI</SelectItem>
                            <SelectItem value="card">Card</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    <Button type="submit" className="w-full">
                      <Zap className="w-4 h-4 mr-2" />
                      Generate Bill
                    </Button>
                  </form>
                </CardContent>
              </Card>

              {/* Bill Preview */}
              <Card className="flex flex-col max-h-[calc(100vh-250px)]">
                <CardHeader className="flex-shrink-0">
                  <CardTitle>Bill Preview</CardTitle>
                </CardHeader>
                <CardContent className="flex-1 overflow-y-auto">
                  {billPreview ? (
                    <div className="space-y-3">
                      <div className="flex justify-between py-2 border-b">
                        <span>Energy Cost ({formData.energy_consumed_kwh} kWh × ₹{settings.price_per_kwh})</span>
                        <span>₹{billPreview.energyCost.toFixed(2)}</span>
                      </div>
                      {billPreview.idleCharges > 0 && (
                        <div className="flex justify-between py-2 border-b">
                          <span>Idle Charges ({formData.idle_duration_mins} mins × ₹{settings.idle_charge_per_min})</span>
                          <span>₹{billPreview.idleCharges.toFixed(2)}</span>
                        </div>
                      )}
                      <div className="flex justify-between py-2 border-b">
                        <span>Service Charge</span>
                        <span>₹{billPreview.serviceCharge.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between py-2 border-b">
                        <span>Subtotal</span>
                        <span>₹{billPreview.subtotal.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between py-2 border-b">
                        <span>GST ({settings.gst_rate}%)</span>
                        <span>₹{billPreview.gstAmount.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between py-2 text-lg font-bold">
                        <span>Total Amount</span>
                        <span className="text-green-600">₹{billPreview.total.toFixed(2)}</span>
                      </div>
                      <div className="mt-4 p-3 bg-muted/50 rounded-lg text-sm">
                        <div className="flex justify-between text-muted-foreground">
                          <span>Grid Cost</span>
                          <span>₹{billPreview.gridCost.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between font-medium text-green-600">
                          <span>Estimated Profit</span>
                          <span>₹{billPreview.profit.toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <Battery className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p>Enter energy consumed to see bill preview</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="ledger">
            <Card>
              <CardHeader>
                <CardTitle>E-Charging Transactions</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-3">Date</th>
                        <th className="text-left p-3">Bill No.</th>
                        <th className="text-left p-3">Charger</th>
                        <th className="text-left p-3">Vehicle</th>
                        <th className="text-right p-3">Energy (kWh)</th>
                        <th className="text-right p-3">Revenue</th>
                        <th className="text-right p-3">Grid Cost</th>
                        <th className="text-right p-3">Profit</th>
                        <th className="text-center p-3">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.map(tx => (
                        <tr key={tx.id} className="border-b hover:bg-muted/50">
                          <td className="p-3">{new Date(tx.date).toLocaleDateString('en-IN')}</td>
                          <td className="p-3 font-mono">{tx.bill_number}</td>
                          <td className="p-3">{tx.charging_point_name}</td>
                          <td className="p-3">{tx.vehicle_number || '-'}</td>
                          <td className="p-3 text-right">{tx.energy_consumed_kwh}</td>
                          <td className="p-3 text-right text-green-600">₹{tx.total_amount?.toLocaleString('en-IN')}</td>
                          <td className="p-3 text-right text-red-500">₹{tx.grid_cost?.toLocaleString('en-IN')}</td>
                          <td className="p-3 text-right font-medium">₹{tx.profit?.toLocaleString('en-IN')}</td>
                          <td className="p-3 text-center">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setSelectedTransaction(tx);
                                setShowBillDialog(true);
                              }}
                            >
                              <Printer className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                      {transactions.length === 0 && (
                        <tr>
                          <td colSpan={9} className="p-8 text-center text-muted-foreground">
                            No transactions yet
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Bill Dialog */}
      <Dialog open={showBillDialog} onOpenChange={setShowBillDialog}>
        <DialogContent className="max-w-md max-h-[90vh] flex flex-col print:max-w-full print:m-0 print:shadow-none">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle>E-Charging Bill</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto pr-2">
            {selectedTransaction && (
              <BillTemplate
                type="echarging"
                transaction={selectedTransaction}
                billSettings={billSettings}
                pumpProfile={pumpProfile}
              />
            )}
          </div>
          <DialogFooter className="flex-shrink-0 border-t pt-4 mt-4 print:hidden">
            <Button variant="outline" onClick={() => setShowBillDialog(false)}>Close</Button>
            <Button onClick={handlePrint}>
              <Printer className="w-4 h-4 mr-2" />
              Print
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default ECharging;
